import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { tournamentService } from '../../../services/tournamentService';
import {
  FIXTURE_SCOPE_OPTIONS,
  GROUP_STAGE_NAME,
  KNOCKOUT_STAGE_OPTIONS,
} from '../../../constants/tournamentStages';

const EMPTY_FORM = {
  scope: 'group',
  groupId: '',
  stageName: 'Quarter Finals',
  customStageName: '',
  homeTeamId: '',
  awayTeamId: '',
  scheduledDate: '',
  scheduledTime: '15:00',
  venue: '',
  matchNotes: '',
};

/**
 * Manually schedule a single tournament fixture for a group or knockout stage.
 */
const ScheduleTournamentGameModal = ({
  isOpen,
  onClose,
  onSuccess,
  tournamentId,
  tournamentName,
  groups = [],
  teams = [],
  initialGroupId = '',
  initialScope = 'group',
}) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSuccess(null);
    setForm({
      ...EMPTY_FORM,
      scope: initialScope === 'knockout' ? 'knockout' : 'group',
      groupId: initialGroupId || '',
      stageName: initialScope === 'knockout' ? 'Quarter Finals' : GROUP_STAGE_NAME,
    });
  }, [isOpen, initialGroupId, initialScope]);

  const activeTeams = useMemo(
    () =>
      (teams || [])
        .filter((tt) => tt.isActive && tt.team?.id)
        .map((tt) => ({
          id: tt.team.id,
          name: tt.team.teamName,
          groupId: tt.groupId || null,
          logoUrl: tt.team.logoUrl,
        })),
    [teams]
  );

  const eligibleTeams = useMemo(() => {
    if (form.scope === 'group' && form.groupId) {
      return activeTeams.filter((team) => team.groupId === form.groupId);
    }
    return activeTeams;
  }, [activeTeams, form.scope, form.groupId]);

  const teamOptions = useMemo(
    () =>
      eligibleTeams.map((team) => ({
        value: team.id,
        label: team.name,
      })),
    [eligibleTeams]
  );

  const homeTeamOptions = useMemo(
    () => teamOptions.filter((opt) => opt.value !== form.awayTeamId),
    [teamOptions, form.awayTeamId]
  );

  const awayTeamOptions = useMemo(
    () => teamOptions.filter((opt) => opt.value !== form.homeTeamId),
    [teamOptions, form.homeTeamId]
  );

  const groupOptions = useMemo(
    () => [
      { value: '', label: 'Select a group' },
      ...(groups || []).map((g) => ({ value: g.id, label: g.name })),
    ],
    [groups]
  );

  const stageOptions = useMemo(
    () => [...KNOCKOUT_STAGE_OPTIONS, { value: '__custom__', label: 'Custom stage…' }],
    []
  );

  const updateField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'scope') {
        if (value === 'group') {
          next.stageName = GROUP_STAGE_NAME;
          next.customStageName = '';
        } else {
          next.groupId = '';
          next.stageName = 'Quarter Finals';
        }
        next.homeTeamId = '';
        next.awayTeamId = '';
      }
      if (field === 'groupId') {
        next.homeTeamId = '';
        next.awayTeamId = '';
      }
      return next;
    });
    setError(null);
    setSuccess(null);
  };

  const resolvedStageName = () => {
    if (form.scope === 'group') return GROUP_STAGE_NAME;
    if (form.stageName === '__custom__') return form.customStageName.trim();
    return form.stageName;
  };

  const buildScheduledDate = () => {
    if (!form.scheduledDate) return null;
    const time = form.scheduledTime || '00:00';
    const iso = new Date(`${form.scheduledDate}T${time}:00`);
    if (Number.isNaN(iso.getTime())) return null;
    return iso.toISOString();
  };

  const validate = () => {
    if (form.scope === 'group') {
      if (!form.groupId) return 'Please select a group';
      if (eligibleTeams.length < 2) {
        return 'Selected group needs at least two active teams';
      }
    }
    if (form.scope === 'knockout') {
      const stage = resolvedStageName();
      if (!stage) return 'Please select or enter a knockout stage name';
    }
    if (!form.homeTeamId || !form.awayTeamId) {
      return 'Please select both home and away teams';
    }
    if (form.homeTeamId === form.awayTeamId) {
      return 'Home and away teams must be different';
    }
    if (!form.scheduledDate) return 'Please select a match date';
    if (!buildScheduledDate()) return 'Invalid date or time';
    return null;
  };

  const handleSubmit = async (e, { keepOpen = false } = {}) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    const stageName = resolvedStageName();
    const isKnockout = form.scope === 'knockout';

    setIsSaving(true);
    try {
      await tournamentService.createGame({
        tournamentId,
        groupId: isKnockout ? null : form.groupId || null,
        homeTeamId: form.homeTeamId,
        awayTeamId: form.awayTeamId,
        scheduledDate: buildScheduledDate(),
        venue: form.venue.trim() || null,
        stageName,
        matchNotes:
          form.matchNotes.trim() ||
          (isKnockout ? `Knockout - ${stageName}` : `Group Stage - ${groups.find((g) => g.id === form.groupId)?.name || ''}`),
        gameStatus: 'scheduled',
      });

      setSuccess('Fixture scheduled successfully');
      onSuccess?.();

      if (keepOpen) {
        setForm((prev) => ({
          ...prev,
          homeTeamId: '',
          awayTeamId: '',
          matchNotes: '',
        }));
      } else {
        onClose?.();
      }
    } catch (err) {
      setError(err.message || 'Failed to schedule fixture');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl card-shadow w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Schedule Game</h2>
            <p className="text-sm text-muted-foreground">
              {tournamentName || 'Tournament'} — manual fixture
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} iconName="X" />
        </div>

        <form onSubmit={(e) => handleSubmit(e, { keepOpen: false })} className="p-6 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg border bg-destructive/10 border-destructive/20 text-destructive text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="px-3 py-2 rounded-lg border bg-success/10 border-success/20 text-success text-sm">
              {success}
            </div>
          )}

          <Select
            label="Fixture type"
            options={FIXTURE_SCOPE_OPTIONS}
            value={form.scope}
            onChange={(value) => updateField('scope', value)}
            required
          />

          {form.scope === 'group' ? (
            <Select
              label="Group"
              options={groupOptions}
              value={form.groupId}
              onChange={(value) => updateField('groupId', value)}
              placeholder="Select a group"
              description={
                groups.length === 0
                  ? 'Create groups first, then assign teams before scheduling group games.'
                  : 'Only teams assigned to this group are available.'
              }
              required
            />
          ) : (
            <>
              <Select
                label="Knockout stage"
                options={stageOptions}
                value={form.stageName}
                onChange={(value) => updateField('stageName', value)}
                required
              />
              {form.stageName === '__custom__' && (
                <Input
                  label="Custom stage name"
                  value={form.customStageName}
                  onChange={(e) => updateField('customStageName', e.target.value)}
                  placeholder="e.g. Classification Match"
                  required
                />
              )}
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Home team"
              options={homeTeamOptions}
              value={form.homeTeamId}
              onChange={(value) => updateField('homeTeamId', value)}
              placeholder={
                form.scope === 'group' && !form.groupId
                  ? 'Select a group first'
                  : 'Select home team'
              }
              searchable
              disabled={form.scope === 'group' && !form.groupId}
              required
            />
            <Select
              label="Away team"
              options={awayTeamOptions}
              value={form.awayTeamId}
              onChange={(value) => updateField('awayTeamId', value)}
              placeholder={
                form.scope === 'group' && !form.groupId
                  ? 'Select a group first'
                  : 'Select away team'
              }
              searchable
              disabled={form.scope === 'group' && !form.groupId}
              required
            />
          </div>

          {form.scope === 'group' && form.groupId && eligibleTeams.length < 2 && (
            <p className="text-sm text-warning flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
              This group needs at least two active teams assigned before you can schedule a match.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Match date"
              value={form.scheduledDate}
              onChange={(e) => updateField('scheduledDate', e.target.value)}
              required
            />
            <Input
              type="time"
              label="Kick-off time"
              value={form.scheduledTime}
              onChange={(e) => updateField('scheduledTime', e.target.value)}
              required
            />
          </div>

          <Input
            label="Venue"
            value={form.venue}
            onChange={(e) => updateField('venue', e.target.value)}
            placeholder="e.g. National Indoor Stadium"
          />

          <Input
            label="Notes (optional)"
            value={form.matchNotes}
            onChange={(e) => updateField('matchNotes', e.target.value)}
            placeholder="Match notes"
          />

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              loading={isSaving}
              iconName="Plus"
              iconPosition="left"
              onClick={(e) => handleSubmit(e, { keepOpen: true })}
            >
              Save & Add Another
            </Button>
            <Button type="submit" variant="default" loading={isSaving} iconName="Calendar" iconPosition="left">
              Schedule Game
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleTournamentGameModal;
